<script>
/* eslint no-console: 0 */
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { defineAsyncComponent, ref } from 'vue';
import { vOnClickOutside } from '@vueuse/components';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import Modal from '../../../../components/Modal.vue';

const EmojiInput = defineAsyncComponent(
  () => import('shared/components/emoji/EmojiInput.vue')
);

export default {
  components: {
    NextButton,
    Modal,
    TagMultiSelectComboBox,
    WootMessageEditor,
    EmojiInput,
  },
  directives: {
    OnClickOutside: vOnClickOutside,
  },
  props: {
    id: { type: Number, default: null },
    edcontent: { type: String, default: '' },
    edshortCode: { type: String, default: '' },
    edLabelIds: { type: Array, default: () => [] },
    edvisibility: { type: String, default: 'public_response' },
    edscopes: { type: Array, default: () => [] },
    onClose: { type: Function, default: () => {} },
  },
  setup() {
    const isEmojiPickerOpen = ref(false);
    return { v$: useVuelidate(), isEmojiPickerOpen };
  },
  data() {
    return {
      editCanned: { showLoading: false },
      shortCode: this.edshortCode,
      content: this.edcontent,
      selectedLabelIds: [...(this.edLabelIds || [])],
      visibility: this.edvisibility,
      selectedUserIds: [],
      selectedTeamIds: [],
      show: true,
    };
  },
  computed: {
    ...mapGetters({
      accountLabels: 'labels/getLabels',
      teams: 'teams/getTeams',
      agents: 'agents/getAgents',
      currentUser: 'getCurrentUser',
    }),
    labelMultiSelectOptions() {
      return this.accountLabels.map(lab => ({
        value: lab.id,
        label: lab.title,
      }));
    },
    agentMultiSelectOptions() {
      return this.agents.map(agent => ({
        value: agent.id,
        label: agent.name,
      }));
    },
    teamMultiSelectOptions() {
      return this.teams.map(team => ({
        value: team.id,
        label: team.name,
      }));
    },
    pageTitle() {
      return `${this.$t('CANNED_MGMT.EDIT.TITLE')} - ${this.edshortCode}`;
    },
    isAdministrator() {
      return this.currentUser?.role === 'administrator';
    },
    isPrivate() {
      return this.visibility === 'private_response';
    },
    isVisibilityActive() {
      return key =>
        this.visibility === key
          ? 'border-[rgba(74,222,128,0.5)] bg-[rgba(74,222,128,0.08)] shadow-[0_0_12px_rgba(74,222,128,0.15)]'
          : 'border-white/10 bg-white/5';
    },
    isFormInvalid() {
      if (this.v$.content.$invalid || this.v$.shortCode.$invalid) return true;
      if (this.isPrivate) {
        const hasScope =
          this.selectedUserIds.length > 0 || this.selectedTeamIds.length > 0;
        if (!hasScope) return true;
      }
      return false;
    },
  },
  mounted() {
    this.$store.dispatch('labels/get');
    this.$store.dispatch('agents/get');
    this.$store.dispatch('teams/get');
    this.$nextTick(() => {
      this.initSelectedScope();
    });
  },
  validations: {
    shortCode: {
      required,
      minLength: minLength(2),
    },
    content: {
      required,
    },
  },
  methods: {
    formatStepNumber(step) {
      return String(step).padStart(2, '0');
    },
    ensureCurrentUserSelected() {
      if (!this.currentUser?.id) return;
      if (!this.selectedUserIds.includes(this.currentUser.id)) {
        this.selectedUserIds = [...this.selectedUserIds, this.currentUser.id];
      }
    },
    initSelectedScope() {
      this.selectedUserIds = this.edscopes
        .flatMap(s => s.user_ids || [])
        .map(Number);
      this.selectedTeamIds = this.edscopes
        .flatMap(s => s.team_ids || [])
        .map(Number);

      if (this.isPrivate) {
        this.ensureCurrentUserSelected();
      }
    },
    onUpdateVisibility(value) {
      this.visibility = value;
      if (value === 'private_response') {
        this.selectedUserIds = [];
        this.selectedTeamIds = [];
        this.ensureCurrentUserSelected();
      } else {
        this.selectedUserIds = [];
        this.selectedTeamIds = [];
      }
    },
    setPageName({ name }) {
      this.v$.content.$touch();
      this.content = name;
    },
    resetForm() {
      this.shortCode = '';
      this.content = '';
      this.selectedLabelIds = [];
      this.visibility = 'public_response';
      this.selectedUserIds = [];
      this.selectedTeamIds = [];
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
    },
    onInsertEmoji(emoji, event) {
      if (event) event.stopPropagation();
      this.content += emoji;
    },
    buildPayload() {
      const payload = {
        id: this.id,
        short_code: this.shortCode,
        content: this.content,
        label_ids: this.selectedLabelIds,
        visibility: this.visibility,
      };
      if (this.isPrivate) {
        payload.user_ids = this.selectedUserIds;
        payload.team_ids = this.selectedTeamIds;
      }
      return payload;
    },
    async editCannedResponse() {
      this.editCanned.showLoading = true;
      try {
        await this.$store.dispatch('updateCannedResponse', this.buildPayload());
        useAlert(this.$t('CANNED_MGMT.EDIT.API.SUCCESS_MESSAGE'));
        this.resetForm();
        setTimeout(() => this.onClose(), 10);
      } catch (error) {
        useAlert(
          error?.message || this.$t('CANNED_MGMT.EDIT.API.ERROR_MESSAGE')
        );
      } finally {
        this.editCanned.showLoading = false;
      }
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col gap-0">
      <div class="px-8 pt-8 pb-6 border-b border-white/10">
        <p
          class="text-xs font-semibold tracking-[0.2em] text-[#4ade80] uppercase mb-1"
        >
          {{ $t('CANNED_MGMT.EDIT.TITLE') }}
        </p>
        <h2 class="text-3xl font-black tracking-wide text-white uppercase">
          {{ pageTitle }}
        </h2>
      </div>

      <form
        class="flex flex-col gap-6 px-8 py-6"
        @submit.prevent="editCannedResponse()"
      >
        <!-- Step 1: Short code -->
        <div class="flex flex-col gap-3">
          <div class="flex items-center gap-2">
            <span class="text-xs font-bold text-[#4ade80] tracking-widest">{{
              formatStepNumber(1)
            }}</span>
            <span
              class="text-xs font-semibold tracking-[0.18em] text-n-slate-10 uppercase"
            >
              {{ $t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.LABEL') }}
            </span>
          </div>
          <div
            class="flex flex-col gap-1 rounded-xl border border-white/10 bg-white/5 px-4 pt-1.5 pb-2 transition-all duration-200 hover:border-[rgba(74,222,128,0.4)] hover:shadow-[0_0_12px_rgba(74,222,128,0.15)] focus-within:border-[rgba(74,222,128,0.5)] focus-within:shadow-[0_0_16px_rgba(74,222,128,0.2)]"
            :class="{ 'border-red-500/50': v$.shortCode.$error }"
          >
            <span
              class="text-[10px] font-semibold tracking-[0.15em] text-[#4ade80] uppercase"
            >
              {{ $t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.LABEL') }}
            </span>
            <input
              v-model="shortCode"
              type="text"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.PLACEHOLDER')"
              class="h-6 bg-transparent border-0 outline-none text-sm text-n-slate-9 placeholder:text-n-slate-8 p-0"
              @input="v$.shortCode.$touch"
            />
          </div>
        </div>

        <div class="border-t border-white/10" />

        <!-- Step 2: Content -->
        <div class="flex flex-col gap-3">
          <div class="flex items-center gap-2">
            <span class="text-xs font-bold text-[#4ade80] tracking-widest">{{
              formatStepNumber(2)
            }}</span>
            <span
              class="text-xs font-semibold tracking-[0.18em] text-n-slate-10 uppercase"
            >
              {{ $t('CANNED_MGMT.EDIT.FORM.CONTENT.LABEL') }}
            </span>
          </div>
          <div
            class="rounded-xl border border-white/10 bg-white/5 px-4 py-3 transition-all duration-200 hover:border-[rgba(74,222,128,0.4)] hover:shadow-[0_0_12px_rgba(74,222,128,0.15)] focus-within:border-[rgba(74,222,128,0.5)] focus-within:shadow-[0_0_16px_rgba(74,222,128,0.2)]"
            :class="{ 'border-red-500/50': v$.content.$error }"
          >
            <span
              class="text-[10px] font-semibold tracking-[0.15em] text-n-slate-10 uppercase block mb-2"
            >
              {{ $t('CANNED_MGMT.EDIT.FORM.CONTENT.LABEL') }}
            </span>
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1"
              :class="{ editor_warning: v$.content.$error }"
              channel-type="Context::Default"
              enable-variables
              :enable-canned-responses="false"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.CONTENT.PLACEHOLDER')"
              @blur="v$.content.$touch"
            />
            <div class="flex items-center pt-2 border-t border-white/10 mt-2">
              <div
                v-on-click-outside="() => (isEmojiPickerOpen = false)"
                class="relative"
                @click.stop
                @keydown.enter.stop
              >
                <NextButton
                  icon="i-ph-smiley-sticker"
                  slate
                  faded
                  sm
                  type="button"
                  @click="isEmojiPickerOpen = !isEmojiPickerOpen"
                />
                <EmojiInput
                  v-if="isEmojiPickerOpen"
                  class="bottom-full mb-1 ltr:left-0 rtl:right-0"
                  :on-click="onInsertEmoji"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="border-t border-white/10" />

        <!-- Step 3: Labels -->
        <div class="flex flex-col gap-3">
          <div class="flex items-center gap-2">
            <span class="text-xs font-bold text-[#4ade80] tracking-widest">{{
              formatStepNumber(3)
            }}</span>
            <span
              class="text-xs font-semibold tracking-[0.18em] text-n-slate-10 uppercase"
            >
              {{ $t('CANNED_MGMT.EDIT.FORM.LABELS.LABEL') }}
            </span>
          </div>
          <p class="text-sm text-n-slate-11 -mt-1">
            {{ $t('CANNED_MGMT.EDIT.FORM.LABELS.HINT') }}
          </p>
          <TagMultiSelectComboBox
            v-if="accountLabels.length"
            v-model="selectedLabelIds"
            class="max-w-xl rounded-xl border border-white/10 bg-white/5 transition-all duration-200 hover:border-[rgba(74,222,128,0.4)] hover:shadow-[0_0_12px_rgba(74,222,128,0.15)] focus-within:border-[rgba(74,222,128,0.5)] focus-within:shadow-[0_0_16px_rgba(74,222,128,0.2)]"
            :options="labelMultiSelectOptions"
            :placeholder="$t('CANNED_MGMT.EDIT.FORM.LABELS.PLACEHOLDER')"
            :search-placeholder="
              $t('CANNED_MGMT.EDIT.FORM.LABELS.SEARCH_PLACEHOLDER')
            "
          />
          <p v-else class="text-sm text-n-slate-11">
            {{ $t('CANNED_MGMT.ADD.FORM.LABELS.EMPTY') }}
          </p>
        </div>

        <div class="border-t border-white/10" />

        <!-- Step 4: Visibility -->
        <div class="flex flex-col gap-3">
          <div class="flex items-center gap-2">
            <span class="text-xs font-bold text-[#4ade80] tracking-widest">{{
              formatStepNumber(4)
            }}</span>
            <span
              class="text-xs font-semibold tracking-[0.18em] text-n-slate-10 uppercase"
            >
              {{ $t('CANNED_MGMT.ADD.FORM.VISIBILITY.LABEL') }}
            </span>
          </div>
          <div class="grid grid-cols-2 gap-3">
            <button
              type="button"
              class="p-3 relative rounded-xl border text-left transition-all duration-200"
              :class="isVisibilityActive('public_response')"
              @click="onUpdateVisibility('public_response')"
            >
              <span
                v-if="visibility === 'public_response'"
                class="i-ph-check-circle-fill absolute text-[#4ade80] top-3 right-3 size-4"
              />
              <p class="text-sm font-semibold text-white m-0">
                {{ $t('CANNED_MGMT.ADD.FORM.VISIBILITY.PUBLIC.LABEL') }}
              </p>
              <p class="text-xs text-n-slate-11 mt-1">
                {{ $t('CANNED_MGMT.ADD.FORM.VISIBILITY.PUBLIC.DESCRIPTION') }}
              </p>
            </button>
            <button
              type="button"
              class="p-3 relative rounded-xl border text-left transition-all duration-200"
              :class="isVisibilityActive('private_response')"
              @click="onUpdateVisibility('private_response')"
            >
              <span
                v-if="visibility === 'private_response'"
                class="i-ph-check-circle-fill absolute text-[#4ade80] top-3 right-3 size-4"
              />
              <p class="text-sm font-semibold text-white m-0">
                {{ $t('CANNED_MGMT.ADD.FORM.VISIBILITY.PRIVATE.LABEL') }}
              </p>
              <p class="text-xs text-n-slate-11 mt-1">
                {{ $t('CANNED_MGMT.ADD.FORM.VISIBILITY.PRIVATE.DESCRIPTION') }}
              </p>
            </button>
          </div>
        </div>

        <!-- Step 5: Scope (only for private) -->
        <template v-if="isPrivate">
          <div class="border-t border-white/10" />

          <div class="flex flex-col gap-3">
            <div class="flex items-center gap-2">
              <span class="text-xs font-bold text-[#4ade80] tracking-widest">{{
                formatStepNumber(5)
              }}</span>
              <span
                class="text-xs font-semibold tracking-[0.18em] text-n-slate-10 uppercase"
              >
                {{ $t('CANNED_MGMT.ADD.FORM.SCOPE.LABEL') }}
              </span>
            </div>

            <div class="flex flex-col gap-1">
              <span class="text-xs text-n-slate-11">
                {{ $t('CANNED_MGMT.ADD.FORM.SCOPE.USER') }}
              </span>
              <TagMultiSelectComboBox
                v-model="selectedUserIds"
                class="max-w-xl rounded-xl border border-white/10 bg-white/5 transition-all duration-200 hover:border-[rgba(74,222,128,0.4)] focus-within:border-[rgba(74,222,128,0.5)]"
                :options="agentMultiSelectOptions"
                :placeholder="$t('CANNED_MGMT.ADD.FORM.SCOPE.PLACEHOLDER')"
              />
              <p class="text-xs text-n-slate-11 mt-1">
                {{ $t('CANNED_MGMT.ADD.FORM.SCOPE.SELF_HINT') }}
              </p>
            </div>

            <div class="flex flex-col gap-1">
              <span class="text-xs text-n-slate-11">
                {{ $t('CANNED_MGMT.ADD.FORM.SCOPE.TEAM') }}
              </span>
              <TagMultiSelectComboBox
                v-model="selectedTeamIds"
                class="max-w-xl rounded-xl border border-white/10 bg-white/5 transition-all duration-200 hover:border-[rgba(74,222,128,0.4)] focus-within:border-[rgba(74,222,128,0.5)]"
                :options="teamMultiSelectOptions"
                :placeholder="$t('CANNED_MGMT.ADD.FORM.SCOPE.PLACEHOLDER')"
              />
            </div>
          </div>
        </template>

        <div class="border-t border-white/10" />

        <div class="flex items-center justify-between gap-3 py-2">
          <NextButton
            faded
            slate
            type="reset"
            variant="link"
            class="h-10 hover:!no-underline hover:text-n-brand"
            :label="$t('CANNED_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            color="teal"
            :label="$t('CANNED_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="isFormInvalid || editCanned.showLoading"
            :is-loading="editCanned.showLoading"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
::v-deep {
  .ProseMirror-menubar {
    @apply hidden;
  }

  .ProseMirror-woot-style {
    @apply min-h-[12.5rem];

    p {
      @apply text-base;
    }
  }
}
</style>
